import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/yanre/Reveal";
import { useLocale } from "@/i18n/LocaleContext";

// SECTION — From Personal Trainer to Studio Owner.
// Entrepreneurial transition chain; no income promises.
const CHAIN = ["pts.journey.0", "pts.journey.1", "pts.journey.2", "pts.journey.3", "pts.journey.4"];
const CHAIN_DEFAULTS = ["Personal Trainer", "Your Own Studio", "Your Own Clients", "Your Own Brand", "Your Own Business"];

export const PTJourney = () => {
  const { t } = useLocale();
  return (
    <section className="section-darker py-24 lg:py-32" data-testid="pt-journey-section">
      <div className="container-edge">
        <Reveal>
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-10 bg-signal" />
            <span className="overline">{t("pts.journey.label", "The Transition")}</span>
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="max-w-4xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-6xl" data-testid="pt-journey-h2">
            {t("pts.journey.h2a", "From Personal Trainer")}{" "}
            <span className="text-signal">{t("pts.journey.h2b", "to Studio Owner.")}</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-titanium/75 sm:text-lg" data-testid="pt-journey-copy">
            {t(
              "pts.journey.copy",
              "Training clients in someone else's gym builds their business. Your own studio builds yours — your space, your client relationships, your brand, your asset. Independence starts with a room and the right equipment."
            )}
          </p>
        </Reveal>

        <div className="mt-14 flex flex-col gap-3 lg:flex-row lg:items-stretch" data-testid="pt-journey-chain">
          {CHAIN.map((key, i) => (
            <div key={key} className="flex items-center gap-3 lg:flex-1">
              <Reveal delay={0.08 * i} className="w-full">
                <div className={`flex h-full items-center border px-5 py-5 ${i === CHAIN.length - 1 ? "border-signal bg-signal/10" : "border-carbon-600 bg-carbon-900"}`} data-testid={`pt-journey-step-${i}`}>
                  <span className="font-display text-base font-bold uppercase tracking-wide text-white sm:text-lg">
                    {t(key, CHAIN_DEFAULTS[i])}
                  </span>
                </div>
              </Reveal>
              {i < CHAIN.length - 1 && <ArrowRight className="h-4 w-4 shrink-0 text-signal max-lg:rotate-90" />}
            </div>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-10 border border-signal/50 bg-signal/5 px-6 py-7 sm:px-10" data-testid="pt-journey-direct">
            <p className="font-display text-2xl font-extrabold uppercase leading-tight tracking-tight text-white sm:text-3xl">
              {t("pts.journey.direct", "Direct From The Manufacturer.")}
            </p>
            <p className="mt-3 max-w-2xl text-sm font-light leading-relaxed text-titanium/70 sm:text-base">
              {t(
                "pts.journey.directCopy",
                "Build your studio with professional equipment purchased directly from YANRE — the manufacturer that engineers and produces every machine."
              )}
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.25}>
          <p className="mt-8 max-w-2xl font-mono text-[11px] uppercase tracking-[0.2em] leading-relaxed text-titanium/45" data-testid="pt-journey-note">
            {t("pts.journey.note", "No promises, no shortcuts — just the structure to turn your craft into a business you own.")}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
